<script lang="ts">
  import { Brush, Clock, Star, ThumbsUp } from '@athena-lib/Icons';
  import { StickerShopHeader, CategoriesMenu } from '@athena-lib/StickerShopHeader';
  import type { Snippet } from 'svelte';
  import { PageSection } from '@stickerapp-org/athena/Page';
  import type { PageSectionBackground } from '@athena-lib/container-context.svelte';
  import { getStickerShopContext } from '$lib/utilities/stickerShopContext';
  import type { FigureTag } from '$lib/server/figures/types';
  import { page as pageState } from '$app/state';

  const {
    children,
    wrapInPageSection = true,
    hideCategories,
  }: { children: Snippet; wrapInPageSection?: boolean; hideCategories?: boolean } = $props();
  const stickerShopMeta = getStickerShopContext();
  const page = $derived(pageState.url.pathname);
  const stickerShopSlug = stickerShopMeta.stickerShopUrls?.baseSlug;
  const tabs = stickerShopMeta.stickerShopUrls
    ? ([
        { name: 'featured' as const, icon: Star, url: stickerShopMeta.stickerShopUrls.featured },
        { name: 'popular' as const, icon: ThumbsUp, url: stickerShopMeta.stickerShopUrls.popular },
        { name: 'latest' as const, icon: Clock, url: stickerShopMeta.stickerShopUrls.recent },
        {
          name: 'by artists' as const,
          icon: Brush,
          url: stickerShopMeta.stickerShopUrls.designers,
        },
      ] satisfies Array<{ name: string; icon: unknown; url: string }>)
    : [];

  const flattenedTags = $derived(
    stickerShopMeta.figureTags.flatMap((x) => [x, ...(x.children ?? [])]),
  );

  const selectedTag: FigureTag | undefined = $derived.by(() => {
    const regex = new RegExp(`${stickerShopSlug}/([^/]+)$`);
    const match = regex.exec(decodeURI(page))?.[1];
    if (!match) {
      return undefined;
    }

    return flattenedTags.find((x) => decodeURI(x.slug) === match);
  });

  const selectedCategories: { title: string; url: string }[] = $derived.by(() => {
    if (!selectedTag || !stickerShopSlug) {
      return [];
    }
    const parent = flattenedTags.find((x) => x.id === selectedTag?.parent_id);
    if (parent) {
      return [
        { title: parent.title, url: stickerShopSlug },
        { title: selectedTag.title, url: parent.slug },
      ];
    }
    return [{ title: selectedTag.title, url: stickerShopSlug }];
  });

  const activeTab = $derived.by(() => {
    if (selectedTag) {
      return undefined;
    }

    const exactMatch = tabs.find(({ url }) => decodeURI(page) === url);
    if (exactMatch) {
      return exactMatch.name;
    }

    // longest non-specific match, we want /sticker-shop/designers/somename to match
    // the tab under /sticker-shop/designers

    return tabs
      .toSorted((a, b) => b.name.length - a.name.length)
      .find(({ url }) => page.startsWith(url))?.name;
  });

  const categories = $derived(
    (selectedTag ? (selectedTag.children ?? []) : stickerShopMeta.figureTags).map((x) => ({
      title: x.title,
      url: `${stickerShopSlug}/${x.slug}`,
    })),
  );

  const stickerShopBackground: PageSectionBackground = 'light';
</script>

<PageSection
  ySpace="none"
  xSpace="none"
  backgroundColor={stickerShopBackground}
  class="max-lg:pb-12 lg:px-6 lg:pb-14 lg:pt-10"
>
  <StickerShopHeader
    stickerShopLogoUrl={stickerShopMeta.stickerShopLogo ?? ''}
    {tabs}
    {activeTab}
  />
</PageSection>
{#if !hideCategories && [...categories, ...selectedCategories].length > 0}
  <PageSection ySpace="none" xSpace="none" backgroundColor={stickerShopBackground} class="pb-10">
    <CategoriesMenu {categories} {selectedCategories}></CategoriesMenu>
  </PageSection>
{/if}

{#if wrapInPageSection}
  <PageSection
    ySpace="none"
    xSpace="none"
    backgroundColor={stickerShopBackground}
    class="px-6 pb-6"
  >
    {@render children()}
  </PageSection>
{:else}
  {@render children()}
{/if}
